import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../../../core/i18n'
import { agentService, utilService } from '../../../../core/services'
import * as constants from '../../../../static/constants'
import * as interfaces from '../../../../static/interfaces'
import { Button, Panel } from '../../../atoms'
import { AgentNav } from '../../../organisms'

declare var window: interfaces.IWindow

interface IRolePermission {
  id: number | null
  agent_permission_id: number
  group: string
  name: string
  description: string
  can_read: boolean
  can_create: boolean
  can_update: boolean
  can_delete: boolean
}

interface IProps {
  agent_role: any
  agent_roles: any
  user: any
}

const CRUD_ACTIONS = [
  { key: 'can_read', label: '閲覧' },
  { key: 'can_create', label: '作成' },
  { key: 'can_update', label: '更新' },
  { key: 'can_delete', label: '削除' },
]

const PermissionsMatrix: React.FC<IProps> = props => {
  const { data: currentRole } = utilService.getDataFromJson(props.agent_role)
  const { data: roles } = agentService.getDataFromJson(props.agent_roles)
  const [selectedRoleId, setSelectedRoleId] = React.useState<number>(currentRole.id)
  const [rolePermissions, setRolePermissions] = React.useState<IRolePermission[]>([])
  const [loading, setLoading] = React.useState(false)

  const loadPermissions = React.useCallback(async (roleId: number) => {
    setLoading(true)
    try {
      const data = await agentService.getPermissionMatrix(roleId)
      setRolePermissions(data.role_permissions)
    } catch (e) {
      console.error('権限データの読み込みに失敗しました', e)
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    loadPermissions(selectedRoleId)
  }, [selectedRoleId, loadPermissions])

  const handleToggle = React.useCallback(
    async (permission: IRolePermission, actionKey: string) => {
      const newValue = !permission[actionKey]
      const updatedPermission = { ...permission, [actionKey]: newValue }

      // 楽観的UI更新
      setRolePermissions(prev =>
        prev.map(p =>
          p.agent_permission_id === permission.agent_permission_id ? updatedPermission : p
        )
      )

      try {
        await agentService.updateRolePermission({
          id: permission.id,
          agent_role_id: selectedRoleId,
          agent_permission_id: permission.agent_permission_id,
          can_read: updatedPermission.can_read,
          can_create: updatedPermission.can_create,
          can_update: updatedPermission.can_update,
          can_delete: updatedPermission.can_delete,
        })
        window.flashMessages.addMessage({
          text: '権限を更新しました',
          type: 'success',
        })
      } catch (e) {
        // ロールバック
        setRolePermissions(prev =>
          prev.map(p => (p.agent_permission_id === permission.agent_permission_id ? permission : p))
        )
        window.flashMessages.addMessage({
          text: '権限の更新に失敗しました',
          type: 'error',
        })
      }
    },
    [selectedRoleId]
  )

  // グループ別にパーミッションをまとめる
  const groupedPermissions = React.useMemo(() => {
    const groups: { [key: string]: IRolePermission[] } = {}
    rolePermissions.forEach(p => {
      if (!groups[p.group]) {
        groups[p.group] = []
      }
      groups[p.group].push(p)
    })
    return groups
  }, [rolePermissions])

  const handleRoleChange = React.useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const roleId = Number(e.target.value)
    setSelectedRoleId(roleId)
    window.history.replaceState(null, '', `/agent/agent_roles/${roleId}/permissions`)
  }, [])

  return (
    <Wrapper>
      <AgentNav active="agent_roles" />
      <section className="Index_Main">
        <Panel title={I18n.t('generic.agent_permissions')}>
          <RoleSelector>
            <label>{I18n.t('generic.agent_roles')}:</label>
            <select value={selectedRoleId} onChange={handleRoleChange}>
              {roles.map(role => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </RoleSelector>

          {loading ? (
            <LoadingMessage>読み込み中...</LoadingMessage>
          ) : (
            <TableWrapper>
              <PermissionTable>
                <thead>
                  <tr>
                    <th className="group-col">{I18n.t('generic.group')}</th>
                    <th className="name-col">{I18n.t('generic.name')}</th>
                    {CRUD_ACTIONS.map(action => (
                      <th key={action.key} className="action-col">
                        {action.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(groupedPermissions).map(([group, perms]) =>
                    perms.map((perm, idx) => (
                      <tr key={perm.agent_permission_id}>
                        {idx === 0 && (
                          <td className="group-col" rowSpan={perms.length}>
                            {group}
                          </td>
                        )}
                        <td className="name-col">
                          <span className="perm-name">{perm.name}</span>
                          {perm.description && (
                            <span className="perm-desc">{perm.description}</span>
                          )}
                        </td>
                        {CRUD_ACTIONS.map(action => (
                          <td key={action.key} className="action-col">
                            <ToggleButton
                              active={perm[action.key]}
                              onClick={() => handleToggle(perm, action.key)}
                              title={`${action.label}: ${perm[action.key] ? 'ON' : 'OFF'}`}
                            >
                              {perm[action.key] ? '○' : '×'}
                            </ToggleButton>
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </PermissionTable>
            </TableWrapper>
          )}

          <BackLink>
            <Button>
              <a href="/agent/agent_roles">{I18n.t('generic.back_to_index')}</a>
            </Button>
          </BackLink>
        </Panel>
      </section>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  min-height: calc(100vh - ${constants.HEADER_HEIGHT + constants.FOOTER_HEIGHT}px);
  margin: 0 auto;
  background-color: ${constants.COLORS.background};
  @media (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
    display: block;
    height: auto;
    min-height: calc(100vh - ${constants.FOOTER_HEIGHT}px);
    padding-top: 55px;
  }

  .Index_Main {
    width: 100%;
    height: 100%;
    max-width: 1340px;
    min-width: 0;
    margin: 0 auto;
    padding: 22px 40px 80px 46px;

    @media (max-width: ${constants.BREAKPOINT_DESKTOP}px) {
      padding: 16px 12px;
      margin: 0 auto;
    }
  }

  .Panel {
    max-width: none;
  }
`

const RoleSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;

  label {
    font-weight: 600;
    font-size: 14px;
  }

  select {
    padding: 8px 12px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 14px;
    min-width: 200px;
  }
`

const TableWrapper = styled.div`
  overflow-x: auto;
`

const PermissionTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  th,
  td {
    padding: 8px 12px;
    border: 1px solid #e0e0e0;
    text-align: center;
  }

  th {
    background: #f5f5f5;
    font-weight: 600;
  }

  .group-col {
    width: 120px;
    font-weight: 600;
    background: #fafafa;
    text-align: left;
  }

  .name-col {
    text-align: left;
    min-width: 150px;

    .perm-name {
      display: block;
      font-weight: 500;
    }

    .perm-desc {
      display: block;
      font-size: 12px;
      color: #888;
    }
  }

  .action-col {
    width: 60px;
  }
`

const ToggleButton = styled.button<{ active: boolean }>`
  width: 32px;
  height: 32px;
  border: 1px solid ${p => (p.active ? constants.COLORS.primary : '#ccc')};
  border-radius: 4px;
  background: ${p => (p.active ? constants.COLORS.primary : '#fff')};
  color: ${p => (p.active ? '#fff' : '#999')};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  line-height: 1;

  &:hover {
    opacity: 0.8;
  }
`

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #888;
`

const BackLink = styled.div`
  margin-top: 24px;
`

export default PermissionsMatrix
